import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  FaBell, 
  FaSpinner, 
  FaChevronLeft, 
  FaChevronRight, 
  FaEye,
  FaPaperclip,
  FaClock,
  FaUser,
  FaMapMarkerAlt,
  FaExclamationCircle,
  FaThumbtack,
  FaFilter,
  FaTimes,
  FaCheckCircle
} from "react-icons/fa";
import api from "../config/api";
import "./Announcements.css";

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [readAnnouncements, setReadAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSingle, setIsLoadingSingle] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 20,
    last_page: 1
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showNewNotification, setShowNewNotification] = useState(false);
  const [newAnnouncementCount, setNewAnnouncementCount] = useState(0);
  const filterRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const notificationTimeoutRef = useRef(null);

  // Function to update BottomNav count
  const updateBottomNavCount = useCallback((count) => {
    setUnreadCount(count);
    // Dispatch event for BottomNav to listen to
    window.dispatchEvent(new CustomEvent('announcementsUpdated', {
      detail: { unreadCount: count }
    }));
    // Save to localStorage for BottomNav fallback
    localStorage.setItem('announcements_unread_count', count.toString());
  }, []);

  // Function to refresh announcements (background - silent)
  const refreshAnnouncements = useCallback(async () => {
    try {
      const response = await api.get(`/announcements?page=${pagination.current_page}&_t=${Date.now()}`);
      
      if (response.data.success && response.data.data) {
        const apiData = response.data.data;
        const announcementsData = apiData.announcements || [];
        
        // Set unread count from API
        const count = apiData.unread_count || 0;
        setUnreadCount(count);
        updateBottomNavCount(count);
        
        // Check if there are new announcements
        const currentAnnouncementIds = new Set(announcements.map(a => a.id));
        const newAnnouncements = announcementsData.filter(a => !currentAnnouncementIds.has(a.id));
        
        if (newAnnouncements.length > 0) {
          // Update new announcement count
          setNewAnnouncementCount(newAnnouncements.length);
          
          // Show brief notification
          setShowNewNotification(true);
          
          // Auto-hide notification after 3 seconds
          if (notificationTimeoutRef.current) {
            clearTimeout(notificationTimeoutRef.current);
          }
          notificationTimeoutRef.current = setTimeout(() => {
            setShowNewNotification(false);
          }, 3000);
          
          // Show browser notification if permission granted
          if (Notification.permission === 'granted') {
            new Notification(`${newAnnouncements.length} New Announcement${newAnnouncements.length > 1 ? 's' : ''}`, {
              body: newAnnouncements[0]?.title || 'New announcement available',
              icon: '/notification-icon.png',
              silent: true // Don't play sound
            });
          }
        }
        
        // Update announcements list silently
        if (Array.isArray(announcementsData)) {
          const sortedAnnouncements = announcementsData.sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
          
          setAnnouncements(sortedAnnouncements);
        }
      }
    } catch (error) {
      // Silent fail - don't show error to user for background refresh
      console.debug("Background refresh failed (silent):", error.message);
    }
  }, [announcements, pagination.current_page, updateBottomNavCount]);

  // Function to load announcements (initial load with loading state)
  const fetchAnnouncements = useCallback(async (page = 1, showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const response = await api.get(`/announcements?page=${page}`);
      
      if (response.data.success && response.data.data) {
        const apiData = response.data.data;
        const announcementsData = apiData.announcements || [];
        
        // Set unread count from API
        const count = apiData.unread_count || 0;
        setUnreadCount(count);
        updateBottomNavCount(count);
        
        setPagination(apiData.pagination || {
          current_page: 1,
          total: 0,
          per_page: 20,
          last_page: 1
        });
        
        if (Array.isArray(announcementsData)) {
          const sortedAnnouncements = announcementsData.sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
          
          setAnnouncements(sortedAnnouncements);
          
          const savedReadAnnouncements = JSON.parse(localStorage.getItem('readAnnouncements') || '[]');
          setReadAnnouncements(savedReadAnnouncements);
        } else {
          setAnnouncements([]);
          setError("Invalid data format");
        }
      } else {
        setError(response.data.message || "Failed to load announcements");
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setError(error.response?.data?.message || "Failed to load. Please try again.");
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [updateBottomNavCount]);

  // Setup auto-refresh interval (every 3 minutes)
  useEffect(() => {
    // Start auto-refresh interval
    refreshIntervalRef.current = setInterval(() => {
      refreshAnnouncements();
    }, 3 * 60 * 1000); // 3 minutes = 180,000 ms

    // Cleanup interval on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, [refreshAnnouncements]);

  // Initial load
  useEffect(() => {
    fetchAnnouncements(1);
    
    // Request notification permission quietly
    if (Notification.permission === 'default') {
      // Don't show popup on load, just prepare for later
      // We'll request permission when needed
    }
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fetchAnnouncements]);

  const fetchSingleAnnouncement = async (id) => {
    setIsLoadingSingle(true);
    try {
      const response = await api.get(`/announcements/${id}`);
      if (response.data.success && response.data.data?.announcement) {
        return response.data.data.announcement;
      }
      return null;
    } catch (error) {
      console.error("Error fetching single announcement:", error);
      return null;
    } finally {
      setIsLoadingSingle(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const response = await api.patch(`/announcements/${id}/read`);
      if (response.data.success) {
        // Update unread count
        const newUnreadCount = Math.max(0, unreadCount - 1);
        setUnreadCount(newUnreadCount);
        updateBottomNavCount(newUnreadCount);
        
        const updatedReadAnnouncements = [...readAnnouncements, id];
        setReadAnnouncements(updatedReadAnnouncements);
        localStorage.setItem('readAnnouncements', JSON.stringify(updatedReadAnnouncements));
        
        setAnnouncements(prev => prev.map(item => 
          item.id === id 
            ? { ...item, is_read: true, read_at: new Date().toISOString() }
            : item
        ));
        
        if (selectedAnnouncement && selectedAnnouncement.id === id) {
          setSelectedAnnouncement(prev => ({
            ...prev,
            is_read: true,
            read_at: new Date().toISOString()
          }));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error marking as read:", error);
      const updatedReadAnnouncements = [...readAnnouncements, id];
      setReadAnnouncements(updatedReadAnnouncements);
      localStorage.setItem('readAnnouncements', JSON.stringify(updatedReadAnnouncements));
      return false;
    }
  };

  const handleOpen = async (announcement) => {
    try {
      const freshAnnouncement = await fetchSingleAnnouncement(announcement.id);
      setSelectedAnnouncement(freshAnnouncement || announcement);
      
      if (!announcement.is_read && !readAnnouncements.includes(announcement.id)) {
        await markAsRead(announcement.id);
      }
    } catch (error) {
      console.error("Error opening:", error);
      setSelectedAnnouncement(announcement);
      if (!announcement.is_read && !readAnnouncements.includes(announcement.id)) {
        await markAsRead(announcement.id);
      }
    }
  };

  const handleClose = () => {
    setSelectedAnnouncement(null);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchAnnouncements(page);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setShowFilterDropdown(false);
  };

  const toggleFilterDropdown = () => {
    setShowFilterDropdown(!showFilterDropdown);
  };

  const getFilteredAnnouncements = () => {
    switch (activeFilter) {
      case 'unread':
        return announcements.filter(a => !a.is_read && !readAnnouncements.includes(a.id));
      case 'pinned':
        return announcements.filter(a => a.is_pinned);
      case 'high':
        return announcements.filter(a => a.priority === 'high' || a.priority === 'urgent');
      default:
        return announcements;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return "";
    }
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return "Just now";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Just now";
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (error) {
      return "Just now";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return '#991b1b'; // Dark red
      case 'high':
        return '#dc2626'; // Red
      case 'normal':
        return '#f59e0b'; // Orange
      case 'low':
        return '#10b981'; // Green
      default:
        return '#6b7280'; // Gray
    }
  };

  const getScopeColor = (scope) => {
    switch (scope?.toLowerCase()) {
      case 'nationwide':
        return '#0055cc'; // Primary blue
      case 'state':
        return '#7c3aed'; // Purple
      case 'lga':
        return '#059669'; // Green
      case 'ward':
        return '#d97706'; // Amber
      case 'polling_unit':
        return '#ec4899'; // Pink
      default:
        return '#6b7280'; // Gray
    }
  };

  const formatScope = (scope) => {
    if (!scope) return "";
    switch (scope.toLowerCase()) {
      case 'nationwide':
        return 'Nationwide';
      case 'polling_unit':
        return 'Polling Unit';
      default:
        return scope.toUpperCase();
    }
  };

  const getFilterLabel = () => {
    switch (activeFilter) {
      case 'all': return 'All Announcements';
      case 'unread': return 'Unread';
      case 'pinned': return 'Pinned';
      case 'high': return 'High Priority';
      default: return 'All Announcements';
    }
  };

  return (
    <div className="announcements-container">
      {/* New Announcement Notification (subtle) */}
      {showNewNotification && (
        <div className="new-announcement-notification">
          <FaCheckCircle className="notification-icon" />
          <span>{newAnnouncementCount} new announcement{newAnnouncementCount > 1 ? 's' : ''} available</span>
          <button 
            className="notification-close"
            onClick={() => setShowNewNotification(false)}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="announcements-header">
        <div className="header-content">
          <div className="header-left">
            <FaBell className="header-icon" />
            <div>
              <h1 className="header-title">Announcements</h1>
              <p className="header-subtitle">Stay updated with latest news</p>
            </div>
          </div>
          
          <div className="header-actions">
            {!isLoading && !error && announcements.length > 0 && (
              <div className="header-stats">
                <div className="stat-item">
                  <span className="stat-number">{pagination.total}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat-item unread-stat">
                  <span className="stat-number">{unreadCount}</span>
                  <span className="stat-label">Unread</span>
                </div>
              </div>
            )}
            
            {/* Filter Dropdown - COMMENTED OUT */}
            {/* {!isLoading && !error && announcements.length > 0 && (
              <div className="filter-dropdown-container" ref={filterRef}>
                <button 
                  className="filter-toggle-btn"
                  onClick={toggleFilterDropdown}
                >
                  <FaFilter className="filter-icon" />
                  <span className="filter-label">{getFilterLabel()}</span>
                  <span className={`dropdown-arrow ${showFilterDropdown ? 'open' : ''}`}>▼</span>
                </button>
                
                {showFilterDropdown && (
                  <div className="filter-dropdown">
                    <div className="dropdown-header">
                      <h4>Filter by</h4>
                      <button 
                        className="close-dropdown"
                        onClick={() => setShowFilterDropdown(false)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <div className="dropdown-options">
                      <button 
                        className={`filter-option ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('all')}
                      >
                        <div className="option-content">
                          <span className="option-text">All Announcements</span>
                        </div>
                      </button>
                      
                      <button 
                        className={`filter-option ${activeFilter === 'unread' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('unread')}
                      >
                        <div className="option-content">
                          <FaEye className="option-icon" />
                          <span className="option-text">Unread</span>
                          {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                        </div>
                      </button>
                      
                      <button 
                        className={`filter-option ${activeFilter === 'pinned' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('pinned')}
                      >
                        <div className="option-content">
                          <FaThumbtack className="option-icon" />
                          <span className="option-text">Pinned</span>
                        </div>
                      </button>
                      
                      <button 
                        className={`filter-option ${activeFilter === 'high' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('high')}
                      >
                        <div className="option-content">
                          <FaExclamationCircle className="option-icon" />
                          <span className="option-text">High Priority</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="announcements-main">
        {isLoading && (
          <div className="loading-state">
            <FaSpinner className="spinner" />
            <p>Loading announcements...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="error-state">
            <FaExclamationCircle className="error-icon" />
            <div>
              <h3>Unable to load</h3>
              <p>{error}</p>
              <button onClick={() => fetchAnnouncements(1)} className="retry-btn">
                Try Again
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && getFilteredAnnouncements().length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📢</div>
            <h3>No announcements</h3>
            <p>
              {activeFilter === 'all' 
                ? "No announcements available" 
                : `No ${activeFilter} announcements`}
            </p>
            {activeFilter !== 'all' && (
              <button 
                className="clear-filter-btn"
                onClick={() => handleFilterChange('all')}
              >
                Show all
              </button>
            )}
          </div>
        )}

        {!isLoading && !error && getFilteredAnnouncements().length > 0 && (
          <>
            <div className="announcements-grid">
              {getFilteredAnnouncements().map((announcement) => (
                <div
                  key={announcement.id}
                  className={`announcement-card ${announcement.is_pinned ? 'pinned' : ''} ${
                    (readAnnouncements.includes(announcement.id) || announcement.is_read) 
                      ? 'read' 
                      : 'unread'
                  }`}
                  onClick={() => handleOpen(announcement)}
                >
                  <div className="card-top">
                    <div className="card-title-row">
                      <h3 className="card-title">{announcement.title}</h3>
                      {!(readAnnouncements.includes(announcement.id) || announcement.is_read) && (
                        <span className="unread-dot"></span>
                      )}
                    </div>
                    {announcement.is_pinned && (
                      <span className="pinned-tag">
                        <FaThumbtack />
                      </span>
                    )}
                  </div>
                  
                  <p className="card-preview">
                    {announcement.content?.length > 100 
                      ? `${announcement.content.substring(0, 100)}...` 
                      : announcement.content}
                  </p>
                  
                  <div className="card-footer">
                    <div className="footer-left">
                      <div className="meta-info">
                        <span className="meta-item time">
                          <FaClock /> {formatRelativeDate(announcement.created_at)}
                        </span>
                        {announcement.admin && (
                          <span className="meta-item author">
                            <FaUser /> {announcement.admin.name}
                          </span>
                        )}
                        {announcement.scope && (
                          <span className="meta-item scope">
                            <FaMapMarkerAlt /> {formatScope(announcement.scope)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="footer-right">
                      {(readAnnouncements.includes(announcement.id) || announcement.is_read) ? (
                        <span className="status read">Read</span>
                      ) : (
                        <span className="status unread">New</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {pagination.last_page > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn prev"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  <FaChevronLeft />
                </button>
                <span className="page-info">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>
                <button 
                  className="pagination-btn next"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {selectedAnnouncement && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {isLoadingSingle ? (
              <div className="modal-loading">
                <FaSpinner className="spinner" />
                <p>Loading details...</p>
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <div className="modal-header-content">
                    <h2 className="modal-title">{selectedAnnouncement.title}</h2>
                    <div className="modal-tags">
                      {selectedAnnouncement.is_pinned && (
                        <span className="tag pinned">
                          <FaThumbtack /> Pinned
                        </span>
                      )}
                      {selectedAnnouncement.priority && (
                        <span 
                          className="tag priority"
                          style={{ backgroundColor: getPriorityColor(selectedAnnouncement.priority) }}
                        >
                          <FaExclamationCircle /> {selectedAnnouncement.priority}
                        </span>
                      )}
                      {selectedAnnouncement.scope && (
                        <span 
                          className="tag scope"
                          style={{ backgroundColor: getScopeColor(selectedAnnouncement.scope) }}
                        >
                          <FaMapMarkerAlt /> {formatScope(selectedAnnouncement.scope)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="modal-body">
                  <div className="modal-meta">
                    <div className="meta-row">
                      <div className="meta-col">
                        <span className="meta-label">Sent</span>
                        <span className="meta-value">
                          <FaClock /> {formatDate(selectedAnnouncement.created_at)}
                        </span>
                      </div>
                      {selectedAnnouncement.admin && (
                        <div className="meta-col">
                          <span className="meta-label">From</span>
                          <span className="meta-value">
                            <FaUser /> {selectedAnnouncement.admin.name}
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedAnnouncement.expires_at && (
                      <div className="meta-row">
                        <div className="meta-col">
                          <span className="meta-label">Expires</span>
                          <span className="meta-value">
                            <FaClock /> {formatDate(selectedAnnouncement.expires_at)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-content-section">
                    <h3 className="content-title">Message</h3>
                    <div className="message-content">
                      {selectedAnnouncement.content}
                    </div>
                  </div>

                  {selectedAnnouncement.attachments && selectedAnnouncement.attachments.length > 0 && (
                    <div className="attachments-section">
                      <h3 className="content-title">
                        <FaPaperclip /> Attachments ({selectedAnnouncement.attachments.length})
                      </h3>
                      <div className="attachments-list">
                        {selectedAnnouncement.attachments.map((attachment, index) => (
                          <div key={index} className="attachment-item">
                            <FaPaperclip className="attachment-icon" />
                            <span className="attachment-name">
                              {attachment.filename || `Attachment ${index + 1}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedAnnouncement.read_at && (
                    <div className="read-info">
                      <span className="read-label">Read on:</span>
                      <span className="read-time">{formatDate(selectedAnnouncement.read_at)}</span>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={handleClose}>
                    Close
                  </button>
                  {!selectedAnnouncement.is_read && !readAnnouncements.includes(selectedAnnouncement.id) && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => markAsRead(selectedAnnouncement.id)}
                    >
                      Mark as Read
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;